import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, MapPin } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTAButtons } from "@/components/site/CTAButtons";
import { GuidePrices } from "@/components/site/GuidePrices";
import { TrustedLocal } from "@/components/site/TrustedLocal";
import { SITE } from "@/config/site";
import faceBgAssetUrl from "@/assets/izhub-face-bg.webp";
const faceBgAsset = { url: faceBgAssetUrl };
import office from "@/assets/service-office.webp";

const SITE_URL = "https://izhubcleaning.uk";
const URL = `${SITE_URL}/office-cleaning-tidworth`;

const NEIGHBOURHOODS = [
  "Tidworth",
  "Tidworth Garrison",
  "Perham Down",
  "Ludgershall",
  "Shipton Bellinger",
  "Bulford",
  "Larkhill",
  "Amesbury",
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "@id": `${URL}#service`,
      serviceType: "Office and Commercial Cleaning",
      name: "Office & Commercial Cleaning in Tidworth",
      description:
        "Contract and one-off office and commercial cleaning in Tidworth, Ludgershall, Perham Down and Amesbury. Insured local cleaners, in-hours or out-of-hours schedules.",
      url: URL,
      areaServed: NEIGHBOURHOODS.map((name) => ({ "@type": "City", name })),
      provider: {
        "@type": "LocalBusiness",
        "@id": `${SITE_URL}/#localbusiness`,
        name: "IZHUB Cleaning Service",
        telephone: ["+44 7869 165458"],
        url: SITE_URL,
        address: {
          "@type": "PostalAddress",
          addressLocality: "Tidworth",
          addressRegion: "Wiltshire",
          addressCountry: "GB",
        },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Office & Commercial Cleaning in Tidworth", item: URL },
      ],
    },
  ],
};

export const Route = createFileRoute("/office-cleaning-tidworth")({
  head: () => ({
    meta: [
      { title: "Office & Commercial Cleaning in Tidworth | IZHUB Cleaning" },
      {
        name: "description",
        content:
          "Office and commercial cleaning in Tidworth, Ludgershall and Amesbury. Insured local cleaners for offices, clinics, salons and studios, in-hours or out-of-hours. Free quote.",
      },
      { property: "og:title", content: "Office & Commercial Cleaning in Tidworth | IZHUB Cleaning" },
      {
        property: "og:description",
        content:
          "Reliable contract cleaning for small offices and business premises across Tidworth and Salisbury Plain. Get a free tailored quote.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
  }),
  component: OfficeTidworth,
});

function OfficeTidworth() {
  return (
    <>
      <PageHero
        title={<>Office &amp; Commercial Cleaning in <span className="rainbow-text">Tidworth</span></>}
        intro="Dependable cleaning for offices, clinics, salons, studios and business premises across Tidworth, Ludgershall and Amesbury, scheduled around your working day."
        withBackground
        backgroundImage={faceBgAsset.url}
      >
        <CTAButtons />
      </PageHero>

      <section className="border-y border-border bg-background/85 backdrop-blur-sm">
        <div className="container-x py-16 md:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
          <img
            src={office}
            alt="Clean, tidy office workspace in Tidworth after a commercial clean"
            loading="lazy"
            className="rounded-3xl w-full h-auto object-cover aspect-[4/3] shadow-soft"
          />
          <div>
            <h2 className="font-display text-3xl md:text-4xl">What a commercial clean covers</h2>
            <ul className="mt-6 space-y-2.5">
              {[
                "Desks, meeting rooms, reception and shared areas",
                "Washrooms: sanitising, descaling and consumable restock",
                "Kitchens and break rooms, including appliance exteriors",
                "Waste and recycling collection to your bin points",
                "Floors: vacuuming, mopping and hard-floor care",
                "Out-of-hours or in-hours visits to suit your team",
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote" search={{ service: "commercial" }} className="btn-primary">Get my instant quote</Link>
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-outline">WhatsApp Us</a>
            </div>
          </div>
        </div>
      </section>

      <GuidePrices />

      <section className="py-16 md:py-24 bg-background/85 backdrop-blur-sm">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl">Business cleaning across the Tidworth area</h2>
          <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
            <p>
              We clean small offices, clinics, salons, studios and retail units throughout Tidworth and the surrounding
              villages. Contracts are built around your opening hours, early mornings, evenings, weekends or during the day
              if a visible presence suits your customers better.
            </p>
            <p>
              Commercial work is priced per site rather than off a fixed list, because a two-desk studio and a busy clinic
              need very different schedules. Tell us the site type, rough floor area, and how many washrooms and kitchens you
              have, and we will send a clear written quote with no obligation.
            </p>
            <p>
              Every cleaner is ID-checked, trained and covered by public liability insurance, and we can supply method
              statements and risk assessments on request.
            </p>
          </div>

          <h3 className="mt-10 font-display text-2xl">Areas we cover</h3>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {NEIGHBOURHOODS.map((a) => (
              <span key={a} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
                <MapPin className="h-3.5 w-3.5 text-primary" />
                {a}
              </span>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Elsewhere in Wiltshire? <Link to="/service-areas" className="text-primary underline underline-offset-4">See all service areas</Link>.
          </p>
        </div>
      </section>

      <TrustedLocal />

      <section className="pb-24">
        <div className="container-x">
          <div className="rounded-3xl bg-sage-soft/60 border border-sage/30 p-8 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl max-w-2xl mx-auto">Get an office cleaning quote</h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Tell us about your premises and we will send a tailored quote, usually within 2 hours.
            </p>
            <div className="mt-8 flex justify-center"><CTAButtons /></div>
          </div>
        </div>
      </section>
    </>
  );
}
